import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../services/api';

const ITENS_POR_PAGINA = 20;
const TIPO_LABEL = {
  REGULAR: 'Avulso',
  MENSALISTA: 'Lily Member',
  SOCIAL: 'Social',
};

export function useAdminDashboardViewModel() {
  const [abaSelecionada, setAbaSelecionada] = useState('geral');
  const [filtroStatus, setFiltroStatus] = useState('Todos');
  const [filtroTipo, setFiltroTipo] = useState('Todos');
  const [filtroPagamento, setFiltroPagamento] = useState('Todos');
  const [busca, setBusca] = useState('');
  const [ordenacao, setOrdenacao] = useState({
    campo: 'horario',
    direcao: 'asc',
  });
  const [itensVisiveis, setItensVisiveis] = useState(ITENS_POR_PAGINA);
  const [hoveredRow, setHoveredRow] = useState(null);

  const [dashboard, setDashboard] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erroApi, setErroApi] = useState(null);

  const [eventos, setEventos] = useState([]);
  const [eventoSelecionado, setEventoSelecionado] = useState('');

  const [novasInscricoes, setNovasInscricoes] = useState([]);
  const ultimoFetchRef = useRef(new Date().toISOString());

  // Resumo global (nao-por-evento): alertas, financeiro consolidado,
  // tendencia e atividade recente. Estado separado do `dashboard` por-evento
  // para que uma falha aqui nao derrube o resto do dashboard.
  const [resumo, setResumo] = useState(null);
  const [carregandoResumo, setCarregandoResumo] = useState(true);
  const [erroResumo, setErroResumo] = useState(null);

  useEffect(() => {
    api
      .get('/admin/dashboard/resumo')
      .then(({ data }) => {
        setResumo(data);
        setErroResumo(null);
      })
      .catch((err) => {
        console.error('[DASHBOARD_RESUMO]', err);
        setErroResumo('Erro ao carregar resumo.');
      })
      .finally(() => setCarregandoResumo(false));
  }, []);

  // Carrega lista de eventos para o seletor
  useEffect(() => {
    api
      .get('/admin/eventos')
      .then(({ data }) => {
        setEventos(data);
        // Seleciona automaticamente o evento mais próximo (futuro ou recente)
        if (data.length > 0) {
          const agora = new Date();
          const futuro = data.find((e) =>
            e.dias?.some((d) => new Date(d.startDate) >= agora),
          );
          setEventoSelecionado(futuro?.id ?? data[0].id);
        }
      })
      .catch(() => {});
  }, []);

  const carregarDashboard = useCallback(
    async (evId) => {
      try {
        const id = evId ?? eventoSelecionado;
        const params = id ? `?eventoId=${id}` : '';
        const { data } = await api.get(`/admin/dashboard${params}`);
        setDashboard(data);
        setErroApi(null);
      } catch (err) {
        console.error('[DASHBOARD]', err);
        setErroApi('Erro ao carregar dados. Verifique sua sessão.');
      } finally {
        setCarregando(false);
      }
    },
    [eventoSelecionado],
  );

  useEffect(() => {
    if (eventoSelecionado !== undefined) {
      setCarregando(true);
      carregarDashboard(eventoSelecionado);
    }
  }, [eventoSelecionado, carregarDashboard]);

  // Poll novas inscrições a cada 20s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const since = ultimoFetchRef.current;
        ultimoFetchRef.current = new Date().toISOString();
        const { data } = await api.get(
          `/admin/inscricoes?since=${encodeURIComponent(since)}`,
        );
        if (data.inscricoes?.length > 0) {
          setNovasInscricoes((prev) => [...data.inscricoes, ...prev]);
          carregarDashboard(eventoSelecionado);
        }
      } catch {
        /* silent */
      }
    }, 20000);
    return () => clearInterval(interval);
  }, [carregarDashboard, eventoSelecionado]);

  const descartarNotificacao = (id) =>
    setNovasInscricoes((prev) => prev.filter((i) => i.id !== id));

  // Reseta a paginação sempre que um filtro/busca/ordenação muda - senão
  // "itensVisiveis" ficaria preso numa posição que não faz sentido pro
  // novo recorte de dados.
  useEffect(() => {
    setItensVisiveis(ITENS_POR_PAGINA);
  }, [filtroStatus, filtroTipo, filtroPagamento, busca, ordenacao]);

  const STATUS_MAP = {
    Confirmado: 'CONFIRMED',
    Pendente: 'PENDING',
    Cancelado: 'CANCELLED',
  };

  // Filtro + ordenação completos, client-side (os dados já vêm todos
  // carregados por carregarDashboard). Separado da paginação: este é o
  // conjunto usado também pro rodapé "Mostrando X de Y" e pro export CSV.
  const inscricoesFiltradas = (() => {
    let lista = dashboard?.inscricoes ?? [];

    if (STATUS_MAP[filtroStatus]) {
      lista = lista.filter((i) => i.status === STATUS_MAP[filtroStatus]);
    }
    if (filtroTipo !== 'Todos') {
      lista = lista.filter((i) => TIPO_LABEL[i.ticketType] === filtroTipo);
    }
    if (filtroPagamento !== 'Todos') {
      lista = lista.filter((i) =>
        filtroPagamento === 'Sem pagamento'
          ? !i.pagamento
          : i.pagamento?.billingType === filtroPagamento,
      );
    }
    if (busca.trim()) {
      const termo = busca.trim().toLowerCase();
      lista = lista.filter((i) =>
        [i.nome, i.email, i.cpf, i.whatsapp].some((campo) =>
          campo?.toLowerCase().includes(termo),
        ),
      );
    }

    const mult = ordenacao.direcao === 'asc' ? 1 : -1;
    lista = [...lista].sort((a, b) => {
      if (ordenacao.campo === 'nome')
        return a.nome.localeCompare(b.nome, 'pt-BR') * mult;
      if (ordenacao.campo === 'valor')
        return (a.amountInCents - b.amountInCents) * mult;
      // 'horario' (default)
      const da = a.horario?.startDate
        ? new Date(a.horario.startDate).getTime()
        : 0;
      const db = b.horario?.startDate
        ? new Date(b.horario.startDate).getTime()
        : 0;
      return (da - db) * mult;
    });

    return lista;
  })();

  const inscricoesPaginadas = inscricoesFiltradas.slice(0, itensVisiveis);
  const temMaisInscricoes = inscricoesFiltradas.length > itensVisiveis;
  const carregarMaisInscricoes = () =>
    setItensVisiveis((n) => n + ITENS_POR_PAGINA);

  function alternarOrdenacao(campo) {
    setOrdenacao((atual) =>
      atual.campo === campo
        ? { campo, direcao: atual.direcao === 'asc' ? 'desc' : 'asc' }
        : { campo, direcao: 'asc' },
    );
  }

  function exportarCsvFiltrado() {
    const cabecalho = [
      'Nome',
      'E-mail',
      'CPF',
      'WhatsApp',
      'Horário',
      'Tipo',
      'Pagamento',
      'Valor',
      'Status',
    ];
    const linhas = inscricoesFiltradas.map((i) => [
      i.nome,
      i.email,
      i.cpf ?? '',
      i.whatsapp ?? '',
      i.horario?.startDate ? formatarData(i.horario.startDate) : '',
      TIPO_LABEL[i.ticketType] ?? i.ticketType,
      i.pagamento?.billingType ?? '',
      (i.amountInCents / 100).toFixed(2).replace('.', ','),
      i.status,
    ]);
    const csv = [cabecalho, ...linhas]
      .map((linha) =>
        linha.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'),
      )
      .join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inscricoes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Oculta valores financeiros na tela toda (tabela, cards, grafico) sem
  // precisar trocar cada local que exibe dinheiro - formatarReais e a unica
  // funcao que todo mundo chama pra formatar centavos, entao mascarar aqui
  // propaga pra tudo de graca.
  const [valoresOcultos, setValoresOcultos] = useState(false);
  const alternarValoresOcultos = () => setValoresOcultos((v) => !v);

  const formatarReais = (centavos) => {
    if (valoresOcultos) return 'R$ ••••';
    return (centavos / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const formatarData = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return {
    abaSelecionada,
    setAbaSelecionada,
    dashboard,
    carregando,
    erroApi,
    resumo,
    carregandoResumo,
    erroResumo,
    novasInscricoes,
    descartarNotificacao,
    inscricoesFiltradas,
    inscricoesPaginadas,
    temMaisInscricoes,
    carregarMaisInscricoes,
    filtroStatus,
    setFiltroStatus,
    filtroTipo,
    setFiltroTipo,
    filtroPagamento,
    setFiltroPagamento,
    busca,
    setBusca,
    ordenacao,
    alternarOrdenacao,
    exportarCsvFiltrado,
    valoresOcultos,
    alternarValoresOcultos,
    hoveredRow,
    setHoveredRow,
    formatarReais,
    formatarData,
    eventos,
    eventoSelecionado,
    setEventoSelecionado,
    recarregar: () => carregarDashboard(eventoSelecionado),
  };
}
